import {useCallback, useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode} from 'react'
import {createPortal} from 'react-dom'
import {Link as RouterLink} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {generateQrBlob, type QrLogoState, type QrStyleState} from '../../../common/utils/qrRenderer'
import type {QrDownloadFormat} from '../../../common/constants/qrDownloadFormats'
import {reportError} from '../../../common/errors/reportError'
import {addBreadcrumb} from '../../../common/errors/breadcrumb'
import {trackEvent} from '../../../common/analytics/trackEvent'
import {downloadFile} from '../../../common/utils/downloadFile'

interface QrExportDialogProps {
  data: string
  style: QrStyleState
  logo: QrLogoState
  format: QrDownloadFormat
  onClose: () => void
}

const FREE_MIN_DURATION_MS = 7000
const FREE_MAX_DURATION_MS = 10000
const FREE_PROGRESS_CEILING = 95
const pickDuration = (): number => FREE_MIN_DURATION_MS + Math.random() * (FREE_MAX_DURATION_MS - FREE_MIN_DURATION_MS)

const iconStrokeProps = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
}

interface AdFeat {
  key: string
  icon: ReactNode
}

// Same pitch as the icon generator's upsell dialog (fe/gui/components/iconGenerator/IconExportDialog.tsx):
// a QR-only visitor likely also needs App Store / Play Store screenshots for
// whatever they're linking to.
const AD_FEATS: AdFeat[] = [
  {
    key: 'mockup',
    icon: (
      <svg {...iconStrokeProps}>
        <rect x="6" y="2" width="12" height="20" rx="2.5" />
        <path d="M11 18h2" />
      </svg>
    ),
  },
  {
    key: 'carousel',
    icon: (
      <svg {...iconStrokeProps}>
        <rect x="2" y="5" width="6" height="14" rx="1.2" />
        <rect x="9" y="5" width="6" height="14" rx="1.2" />
        <rect x="16" y="5" width="6" height="14" rx="1.2" />
      </svg>
    ),
  },
  {
    key: 'free',
    icon: (
      <svg {...iconStrokeProps}>
        <path d="M20.6 13.4l-7.2 7.2a2 2 0 01-2.8 0L3 12.8V4h8.8l8.8 8.8a2 2 0 010 2.6z" />
        <circle cx="8.5" cy="8.5" r="1.3" />
      </svg>
    ),
  },
]

const arrowIcon = (
  <svg
    width={13}
    height={13}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M5 12h14" />
    <path d="M13 5l7 7-7 7" />
  </svg>
)

const QrExportDialog = ({data, style, logo, format, onClose}: QrExportDialogProps) => {
  const {t} = useTranslation()
  const [progress, setProgress] = useState(0)
  const [ready, setReady] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const blobRef = useRef<Blob | null>(null)
  const startRef = useRef<number | null>(null)
  const durationRef = useRef<number | null>(null)

  // Kick off the real export in parallel with the synthetic timer. Parent
  // mounts/unmounts this component per open, so no reset effect is needed.
  useEffect(() => {
    let cancelled = false
    addBreadcrumb('qr-gen', 'export-start', 'info', {format: format.extension})
    trackEvent('upsell_shown', {surface: 'qr_gen'})
    generateQrBlob(data, style, logo, format.extension)
      .then((blob) => {
        if (!cancelled) {
          blobRef.current = blob
          addBreadcrumb('qr-gen', 'export-ready', 'info', {size: blob.size})
        }
      })
      .catch((err) => {
        if (!cancelled) {
          reportError(err, 'qr-gen-export', {format: format.extension})
          setError(t('qrGenerator.download.failed'))
        }
      })
    return () => {
      cancelled = true
    }
  }, [data, style, logo, format, t])

  // Drive synthetic progress (ease-out cubic)
  useEffect(() => {
    if (ready) return
    if (startRef.current === null) startRef.current = performance.now()
    if (durationRef.current === null) durationRef.current = pickDuration()

    let rafId = 0
    const tick = () => {
      const start = startRef.current ?? performance.now()
      const duration = durationRef.current ?? FREE_MIN_DURATION_MS
      const elapsed = performance.now() - start
      const ratio = Math.min(1, elapsed / duration)
      const eased = 1 - Math.pow(1 - ratio, 3)
      const exportDone = blobRef.current !== null
      const ceiling = exportDone ? 100 : FREE_PROGRESS_CEILING
      setProgress(Math.min(eased * 100, ceiling))

      if (elapsed >= duration && exportDone) {
        setProgress(100)
        setReady(true)
        return
      }
      rafId = requestAnimationFrame(tick)
    }
    rafId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafId)
  }, [ready])

  // Esc to close
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [onClose])

  const handleDownload = useCallback(async () => {
    const blob = blobRef.current
    if (!blob) return
    await downloadFile(blob, `qr-code.${format.fileExt}`)
    trackEvent('qr_download_completed', {format: format.extension, size_bytes: blob.size, gated: true})
    onClose()
  }, [format, onClose])

  const handleOverlayClick = useCallback(
    (e: ReactMouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose()
    },
    [onClose],
  )

  return createPortal(
    <div className="qrg-modal-overlay" role="dialog" aria-modal="true" onClick={handleOverlayClick}>
      <div className="qrg-modal">
        <header className="qrg-modal-header">
          <h3 className="qrg-modal-title">
            {ready ? t('qrGenerator.dialog.readyTitle') : t('qrGenerator.dialog.title')}
          </h3>
          <button
            type="button"
            className="qrg-modal-close"
            onClick={onClose}
            aria-label={t('qrGenerator.dialog.close')}
          >
            ×
          </button>
        </header>

        <div className="qrg-modal-body">
          <div className="qrg-modal-progress-side">
            <p className="qrg-modal-sub">{ready ? t('qrGenerator.dialog.ready') : t('qrGenerator.dialog.preparing')}</p>

            {error && <p className="qrg-error">{error}</p>}

            {!ready && !error && (
              <div className="qrg-modal-progress-row">
                <div
                  className="qrg-modal-progress"
                  role="progressbar"
                  aria-label={t('qrGenerator.dialog.progressLabel')}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(progress)}
                >
                  <div className="qrg-modal-progress-bar" style={{transform: `translateX(${-(100 - progress)}%)`}} />
                </div>
                <span className="qrg-modal-progress-value">{Math.round(progress)}%</span>
              </div>
            )}

            {ready && !error && (
              <button type="button" className="qrg-btn qrg-btn-primary" onClick={handleDownload}>
                {t('qrGenerator.dialog.download')}
              </button>
            )}
          </div>

          <aside className="qrg-modal-ad" aria-label={t('qrGenerator.dialog.ad.ariaLabel')}>
            <div className="qrg-ad-header">
              <div className="qrg-ad-eyebrow">{t('qrGenerator.dialog.ad.eyebrow')}</div>
              <div className="qrg-ad-heading">{t('qrGenerator.dialog.ad.heading')}</div>
              <p className="qrg-ad-sub">{t('qrGenerator.dialog.ad.sub')}</p>
            </div>
            <ul className="qrg-ad-feats">
              {AD_FEATS.map(({key, icon}) => (
                <li key={key} className="qrg-ad-feat">
                  <span className="qrg-ad-feat-icon" aria-hidden>
                    {icon}
                  </span>
                  <div className="qrg-ad-feat-body">
                    <div className="qrg-ad-feat-label">{t(`qrGenerator.dialog.ad.feats.${key}.label`)}</div>
                    <div className="qrg-ad-feat-desc">{t(`qrGenerator.dialog.ad.feats.${key}.desc`)}</div>
                  </div>
                </li>
              ))}
            </ul>
            {/* Opens in a new tab: clicking this while the export is still in progress
                must not navigate the current tab away and interrupt it. */}
            <RouterLink to="/free" className="qrg-ad-cta" target="_blank" rel="noopener noreferrer">
              <span className="qrg-ad-cta-main">
                {t('qrGenerator.dialog.ad.cta')}
                {arrowIcon}
              </span>
              <span className="qrg-ad-cta-hint">{t('qrGenerator.dialog.ad.ctaHint')}</span>
            </RouterLink>
          </aside>
        </div>
      </div>
    </div>,
    document.body,
  )
}

export default QrExportDialog
